import * as stylex from "@stylexjs/stylex";
import * as scale from "d3-scale";
import { tuple } from "fp-ts/lib/function";
import { pipe } from "fp-ts/lib/pipeable";
import { motion, transform } from "framer-motion";
import React from "react";
import ColorLegend from "../charts/lib/ColorLegend";
import { findClosest } from "../charts/lib/utils";
import { ChartTooltip, TooltipGridBodyWithLegend } from "../components/tooltip";
import { useContentRect, useMedia, usePointerPosition } from "../hooks";
import { extentNumber, insertionOrderSet } from "../lib";
import { breakpoints, colors } from "../materials/consts.stylex";
import { layout } from "../materials/layout.stylex";
import { typography } from "../materials/typography.stylex";
import { An, Ar, O } from "../prelude";
import { YAnnotations } from "./annotations";
// -----------------------------------------------------------------------------
// Helpers
function stackExtent(stack, acc) {
    return stack.reduce(([pMin, pMax], x_)=>{
        const x = acc(x_);
        return tuple(x < pMin ? x : pMin, x > pMax ? x : pMax);
    }, tuple(Infinity, -Infinity));
}
const popperOptions = {
    positionFixed: true
};
const MOAr = O.getMonoid(Ar.getMonoid());
// -----------------------------------------------------------------------------
// Components
const styles = {
    xLabel: {
        kDwRjp: "x13ylgtg",
        kcca2Q: "x1gr4c9z",
        kR6KzO: "xw6cgo0",
        $$css: true
    },
    yLabel: {
        kDwRjp: "x13ylgtg",
        kcca2Q: "x1gr4c9z",
        kR6KzO: "x233ees",
        $$css: true
    }
};
function foldDotStyle(style, pattern) {
    return pattern[style]();
}
export const VerticalDotPlot = React.memo(VerticalDotPlot_);
function VerticalDotPlot_({ colorScale, colorLegendValues, marginLeft, labelStyleX = "default", dotStyle = "default", highlightItems = O.none, ...p }) {
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const [pointerPositionRef, pointerPosition] = usePointerPosition();
    const showYAxis = !p.hideYAxis;
    const margin = React.useMemo(()=>({
            top: 20,
            right: 0,
            bottom: screenSDown ? 12 : 30,
            left: showYAxis ? marginLeft != null ? marginLeft : 30 : 0
        }), [
        screenSDown,
        marginLeft,
        showYAxis
    ]);
    const outerHeight = p.height + margin.top + margin.bottom;
    const innerHeight1 = p.height;
    const outerWidth = p.width;
    const innerWidth1 = p.width - margin.left - margin.right;
    const domainX = React.useMemo(()=>insertionOrderSet(p.data, p.getX), [
        p.data,
        p.getX
    ]);
    const domainY = React.useMemo(()=>p.domainY != null ? p.domainY : extentNumber(p.data, p.getValue), [
        p.data,
        p.domainY,
        p.getValue
    ]);
    const stacks = React.useMemo(()=>An.groupBy(p.getX)(p.data), [
        p.data,
        p.getX
    ]);
    const xScale = React.useMemo(()=>scale.scalePoint().domain(domainX).range([
            dotRadius(dotStyle),
            innerWidth1 - dotRadius(dotStyle)
        ]), [
        domainX,
        innerWidth1,
        dotStyle
    ]);
    const xScaleInvert = React.useMemo(()=>{
        const steps = domainX.map((x)=>({
                domain: x,
                range: xScale(x)
            }));
        return (pos)=>findClosest(steps, (x)=>x.range, pos).domain;
    }, [
        domainX,
        xScale
    ]);
    const [yScale, yTicks] = React.useMemo(()=>{
        const y = scale.scaleLinear().domain(domainY).range([
            innerHeight1,
            0
        ]);
        return [
            y,
            y.ticks(screenMDown ? 4 : 6)
        ];
    }, [
        screenMDown,
        domainY,
        innerHeight1
    ]);
    const selectedItems = pipe(MOAr.concat(pipe(pointerPosition, O.map((pos)=>An.of(xScaleInvert(pos.x)))), highlightItems));
    return <div>
      <svg height={outerHeight} width={outerWidth}>
        {showYAxis && <g key={"ylabels"} transform={`translate(0, ${margin.top})`}>
            <YLabels yTicks={yTicks} yScale={yScale} xEnd={margin.left - 4} formatValue={p.formatAxisValue || p.formatValue}/>
          </g>}
        <g key={"main"} transform={`translate(${margin.left}, ${margin.top})`}>
          <XTicks labelStyleX={labelStyleX} domainX={domainX} xScale={xScale} formatX={p.formatX}/>
          <YTicks yTicks={yTicks} yScale={yScale} yUnitLabel={p.yUnitLabel}/>
          {p.yAnnotations && <YAnnotations width={innerWidth1 + margin.right} yScale={yScale} yFormat={p.formatValue} annotations={p.yAnnotations}/>}
          {domainX.map((key)=>{
        const stack = stacks[key];
        const x = xScale(p.getX(stack[0]));
        const isSelected = pipe(selectedItems, O.fold(()=>false, (sel)=>sel.includes(key)));
        return <DotGroup key={key} name={key} stack={stack} x={x} isSelected={isSelected} yScale={yScale} colorScale={colorScale} getValue={p.getValue} getColor={p.getColor} formatX={p.formatX} formatValue={p.formatValue} domainX={domainX} showYExtent={p.showYExtent} dotStyle={dotStyle}/>;
    })}
          {p.showTooltip && <rect ref={pointerPositionRef} x={0} y={0} width={innerWidth1} height={yScale.range()[0]} fill="transparent"/>}
        </g>
      </svg>
      {p.showLegend && <ColorLegend inline maxWidth={"100%"} values={colorLegendValues}/>}
    </div>;
}
export function VerticalDotPlotAuto(props) {
    const [ref, contentRect] = useContentRect();
    return <div ref={ref} {...stylex.props(layout.fullWidth)}>
      {contentRect.width > 0 && <VerticalDotPlot {...props} width={contentRect.width}/>}
    </div>;
}
// -----------------------------------------------------------------------------
export function useColorScaleVDP({ colorPalette, formatColor, ...p }) {
    const domainColor = React.useMemo(()=>p.domainColor || insertionOrderSet(p.data, p.getColor), [
        p.data,
        p.getColor,
        p.domainColor
    ]);
    const colorScale = React.useMemo(()=>scale.scaleOrdinal(colorPalette(domainColor.length)).domain(domainColor), [
        colorPalette,
        domainColor
    ]);
    const colorLegendValues = React.useMemo(()=>{
        return domainColor.map((key)=>({
                color: colorScale(key),
                label: formatColor(key)
            }));
    }, [
        formatColor,
        colorScale,
        domainColor
    ]);
    return [
        colorScale,
        colorLegendValues
    ];
}
// -----------------------------------------------------------------------------
function dotRadius(dotStyle) {
    return foldDotStyle(dotStyle, {
        default: ()=>8,
        medium: ()=>7.5,
        small: ()=>6.5
    });
}
function DotGroup_({ name, stack, x, isSelected, yScale, colorScale, getValue, getColor, formatX, formatValue, domainX, showYExtent, dotStyle = "default" }) {
    const [y1, y2] = stackExtent(stack, getValue).map(yScale);
    const radius = dotRadius(dotStyle);
    const [maxDuration, maxDots] = [
        0.4,
        stack.length
    ];
    return <motion.g>
      {showYExtent && isSelected && stack.map((d)=>{
        const y = yScale(getValue(d));
        const color = colorScale(getColor(d));
        return <motion.line key={`y-highlight-${color}`} className="xnpr05h xlz5em3 xc03x0k" x1={0} x2={innerWidth} y1={y} y2={y} stroke={color}/>;
    })}
      <motion.line className="x1q4kd6z xc03x0k" initial={{
        x1: x,
        x2: x,
        y1,
        y2,
        strokeWidth: isSelected ? 3 : 1,
        opacity: 0
    }} animate={{
        x1: x,
        x2: x,
        y1,
        y2,
        strokeWidth: isSelected ? 3 : 1,
        opacity: [
            0,
            1
        ]
    }} transition={{
        ease: "easeOut"
    }}/>
      {stack.map((d, index)=>{
        const cy = yScale(getValue(d));
        const { r, fill, strokeWidth } = foldDotStyle(dotStyle, {
            default: ()=>({
                    r: radius,
                    fill: colorScale(getColor(d)),
                    strokeWidth: 0
                }),
            medium: ()=>({
                    r: isSelected ? radius - 3 / 2 : radius / 2.5,
                    fill: isSelected ? "#fff" : colorScale(getColor(d)),
                    strokeWidth: isSelected ? 3 : 0
                }),
            small: ()=>({
                    r: isSelected ? radius - 3 / 2 : radius / 2.6,
                    fill: isSelected ? "#fff" : colorScale(getColor(d)),
                    strokeWidth: isSelected ? 3 : 0
                })
        });
        return <motion.circle key={`dot-${getColor(d)}`} stroke={colorScale(getColor(d))} initial={{
            cx: x,
            cy,
            r,
            fill,
            strokeWidth,
            opacity: 0
        }} animate={{
            cx: x,
            cy,
            r,
            fill,
            strokeWidth,
            opacity: [
                0,
                1
            ]
        }} transition={{
            ease: "easeOut",
            delay: transform(index, [
                0,
                maxDots
            ], [
                0,
                maxDuration
            ])
        }}/>;
    })}
      {isSelected && <ChartTooltip title={formatX(name, 0 /* Special case: we always want the rendering for the 0 position */ , domainX)} content={<TooltipGridBodyWithLegend rows={stack.map((x)=>[
            colorScale(getColor(x)),
            getColor(x),
            formatValue(getValue(x))
        ])}/>} placement="top" visible={isSelected} hideOnClick={false} popperOptions={popperOptions}>
          <circle cx={x} cy={y2} r={radius} fill={"transparent"}/>
        </ChartTooltip>}
    </motion.g>;
}
const DotGroup = React.memo(DotGroup_);
const XTicks = React.memo(({ labelStyleX, domainX, xScale, formatX })=>{
    return labelStyleX !== "none" ? <>
        {domainX.map((tick, i)=>{
        const x = xScale(tick) || 0;
        return <React.Fragment key={`xaxis-${tick}`}>
              <line className="xnpr05h x9zv0gs xc03x0k" x1={x} x2={x} y1={innerHeight} y2={innerHeight + 4}/>
              <text {...stylex.props(typography.axis, styles.xLabel)} x={x} y={innerHeight} dy={"1.2em"}>
                {formatX(tick, i, domainX)}
              </text>
            </React.Fragment>;
    })}
      </> : null;
});
const YTicks = React.memo(({ yTicks, yScale, yUnitLabel })=>{
    return <>
        {yTicks.map((tick, i)=>{
        const isLast = i === yTicks.length - 1;
        const y = yScale(tick);
        return <React.Fragment key={`yaxis-${tick}`}>
              <motion.line className="xnpr05h x9zv0gs xc03x0k" transition={{
            ease: "easeOut"
        }} x1={0} x2={innerWidth} y1={y} y2={y} initial={{
            y1: y,
            y2: y,
            opacity: 0
        }} animate={{
            y1: y,
            y2: y,
            opacity: [
                0,
                1
            ]
        }}/>
              {yUnitLabel && isLast && <motion.text {...stylex.props(typography.axis, styles.yLabel)} transition={{
            ease: "easeOut"
        }} x={0} y={y} dy={"-0.8em"} initial={{
            y
        }} animate={{
            y
        }}>
                  {yUnitLabel}
                </motion.text>}
            </React.Fragment>;
    })}
      </>;
});
const YLabels = React.memo(({ yTicks, yScale, xEnd, formatValue })=>{
    return <>
        {yTicks.map((tick)=>{
        const y = yScale(tick);
        return <React.Fragment key={`ylabel-${tick}`}>
              <motion.line className="xnpr05h x1q4kd6z xc03x0k" transition={{
            ease: "easeOut"
        }} x1={0} x2={xEnd} y1={y} y2={y} initial={{
            y1: y,
            y2: y,
            opacity: 0
        }} animate={{
            y1: y,
            y2: y,
            opacity: [
                0,
                1
            ]
        }}/>
              <motion.text {...stylex.props(typography.axis, styles.yLabel)} initial={{
            y,
            opacity: 0
        }} animate={{
            y,
            opacity: [
                0,
                1
            ]
        }} transition={{
            ease: "easeOut"
        }} dy={"-0.8em"}>
                {formatValue(tick)}
              </motion.text>
            </React.Fragment>;
    })}
      </>;
});
